// FILE: DevServerControl.logic.ts
// Purpose: Presentation rules for the preview control that are worth testing on
// their own — what the collapsed chip says, and how much of a public grant is
// left. Kept out of the component so neither needs a DOM to be checked.
import type { DevServerStatus } from "@taskin-code/contracts";

import { remainingMsUntil } from "~/lib/relativeTime";

export type DevServerRuntimeState = DevServerStatus["state"];

/** Colour and label for each runtime state, kept together so they cannot drift. */
const STATE_PRESENTATION = {
  idle: { label: "Preview", dot: "bg-muted-foreground/40" },
  booting: { label: "Starting", dot: "bg-amber-500" },
  running: { label: "Live", dot: "bg-emerald-500" },
  error: { label: "Failed", dot: "bg-red-500" },
} as const;

export interface PreviewChipPresentation {
  readonly label: string;
  readonly dot: string;
  /** True while the preview answers callers with no Taskin session. */
  readonly isPublic: boolean;
}

/**
 * Milliseconds of public access left, or null when there is no live grant.
 *
 * Kept as a name of its own over the shared countdown helper: "is this preview
 * published right now" is the question, and callers read better asking it than
 * asking how many milliseconds are left on a timestamp.
 */
export const publicAccessRemainingMs = (
  publicUntil: string | null | undefined,
  nowMs: number,
): number | null => remainingMsUntil(publicUntil, nowMs);

/**
 * How long is left, in the coarsest unit that is still true.
 *
 * Coarse on purpose: the grant lasts a day, and a countdown to the second would
 * invite reading it as a deadline rather than as "this closes itself".
 */
export const formatPublicCountdown = (
  publicUntil: string | null | undefined,
  nowMs: number,
): string | null => {
  const remaining = publicAccessRemainingMs(publicUntil, nowMs);
  if (remaining === null) return null;
  const minutes = Math.floor(remaining / 60_000);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
};

/**
 * What the collapsed control shows.
 *
 * A published preview says so in the chip rather than only inside the popover:
 * the failure mode worth designing against is somebody publishing one and never
 * thinking about it again, and a badge nobody has to open a panel to see is the
 * cheapest reminder there is.
 */
export const resolvePreviewChip = (input: {
  readonly state: DevServerRuntimeState;
  readonly publicUntil: string | null | undefined;
  readonly nowMs: number;
}): PreviewChipPresentation => {
  const base = STATE_PRESENTATION[input.state];
  const isPublic = publicAccessRemainingMs(input.publicUntil, input.nowMs) !== null;
  if (!isPublic) return { ...base, isPublic: false };
  return {
    label: input.state === "running" ? "Public" : `${base.label} · Public`,
    dot: "bg-amber-400",
    isPublic: true,
  };
};
